import { Text, Row, Avatar, Tooltip } from "@hightouch/htui";
import { SkeletonItem } from "src/components/skeletons";

import { formatDate } from "src/utils/time";
import type { TableColumn } from "src/ui/table/table";

interface LastUpdatedCellProps {
  timestamp?: string | null;
  name?: string | null;
  /**
   * Whether a user was ever attributed to this resource. When `false`, the
   * resource has no stored user attribution (e.g. system-created journey
   * syncs, where `created_by`/`updated_by` are null) rather than an attributed
   * user who has since been removed from the workspace. When `undefined`, the
   * caller didn't provide attribution ids, so the legacy "removed" message is
   * shown for backwards compatibility.
   */
  hasUserAttribution?: boolean;
}

export const LastUpdatedCell = (props: LastUpdatedCellProps) => {
  const { name, timestamp, hasUserAttribution } = props;

  const displayTime = timestamp ? (
    <Text fontWeight="medium" color="text.secondary" size="sm">
      {formatDate(timestamp)}
    </Text>
  ) : (
    <Text fontWeight="medium" size="sm">
      -
    </Text>
  );
  const displayName = name ? (
    <Avatar name={name} />
  ) : hasUserAttribution === false ? (
    <Tooltip message="No user is associated with this record">
      <Avatar name={undefined} />
    </Tooltip>
  ) : (
    <Tooltip message="User was removed from this workspace">
      <Avatar name={undefined} />
    </Tooltip>
  );

  return (
    <Row align="center" gap={2} width="100%">
      {displayName}
      {displayTime}
    </Row>
  );
};

const shared = {
  name: "Last updated",
  max: "max-content",
};

export const LastUpdatedSkeleton = ({
  showAvatar = true,
}: {
  showAvatar?: boolean;
}) => (
  <Row align="center" gap={2}>
    <SkeletonItem borderRadius="md" height={5} width="55px" />
    {showAvatar && <SkeletonItem borderRadius="50%" h={8} w={8} />}
  </Row>
);

export const LastUpdatedColumn: TableColumn = {
  ...shared,
  skeleton: <LastUpdatedSkeleton />,
  cell: ({
    updated_at,
    created_at,
    updated_by_user,
    created_by_user,
    updated_by,
    created_by,
  }) => {
    return (
      <LastUpdatedCell
        timestamp={updated_at || created_at}
        name={updated_by_user?.name || created_by_user?.name}
        hasUserAttribution={resolveHasUserAttribution(updated_by, created_by)}
      />
    );
  },
};

/**
 * Distinguishes "user was removed" from "no attribution stored" using the raw
 * `created_by`/`updated_by` id columns.
 * - a non-null id => a user was attributed (and, if its name is missing, that
 *   user was removed from the workspace) => `true`
 * - both ids explicitly `null` => no user attribution was ever stored => `false`
 * - ids absent (`undefined`, e.g. the query didn't select them) => `undefined`,
 *   so the cell keeps its legacy "removed" message
 */
export const resolveHasUserAttribution = (
  updatedBy: string | null | undefined,
  createdBy: string | null | undefined,
): boolean | undefined => {
  if (updatedBy != null || createdBy != null) {
    return true;
  }
  if (updatedBy === null && createdBy === null) {
    return false;
  }
  return undefined;
};

export const LastUpdatedColumnAlternate: TableColumn = {
  ...shared,
  skeleton: <LastUpdatedSkeleton />,
  cell: ({ updated_at, created_at, updated_by, created_by }) => {
    return (
      <LastUpdatedCell
        timestamp={updated_at || created_at}
        name={updated_by?.name || created_by?.name}
      />
    );
  },
};
